import { Alert, Button, Card, Col, Divider, Drawer, Empty, Modal, QRCode, Row, Space, Spin, Table, Tag, Typography, message } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createCheckout, getBillingPlans, getSubscription, getSubscriptionHistory, reconcilePaymentStatus } from '../api';
import { resolveErrorMessage } from '../utils/errorMessage';
import './Billing.css';

const HISTORY_PAGE_SIZE = 8;

const formatPrice = (cents) => {
  const value = Number(cents || 0) / 100;
  return value % 1 === 0 ? `${value}` : value.toFixed(2);
};

const resolveDurationLabel = (days) => {
  const d = Number(days || 0);
  if (d >= 36500) return '终身';
  if (d === 30) return '1个月';
  if (d === 90) return '3个月';
  if (d === 180) return '6个月';
  if (d === 365) return '1年';
  if (d > 0) return `${d}天`;
  return '-';
};

const formatDateTime = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '-';

  // Prefer preserving the backend-provided wall-clock time text.
  const m = raw.match(/^(\d{4}-\d{2}-\d{2})[T\s](\d{2}:\d{2}:\d{2})/);
  if (m) return `${m[1]} ${m[2]}`;

  const dt = new Date(raw);
  if (Number.isNaN(dt.getTime())) return raw;
  const y = dt.getFullYear();
  const mon = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  const h = String(dt.getHours()).padStart(2, '0');
  const min = String(dt.getMinutes()).padStart(2, '0');
  const sec = String(dt.getSeconds()).padStart(2, '0');
  return `${y}-${mon}-${d} ${h}:${min}:${sec}`;
};

const formatPaymentAmount = (amountCents, currency = 'CNY') => {
  const amount = formatPrice(amountCents);
  if (String(currency || '').toUpperCase() === 'CNY') {
    return `¥${amount}`;
  }
  return `${currency} ${amount}`;
};

const resolveTradeStateMeta = (status, tradeState) => {
  const normalizedStatus = String(status || '').toLowerCase();
  const normalizedState = String(tradeState || '').toUpperCase();
  if (normalizedStatus === 'success' || normalizedState === 'SUCCESS') {
    return { text: '已支付', color: 'green' };
  }
  if (normalizedState === 'USERPAYING' || normalizedState === 'NOTPAY') {
    return { text: '待支付', color: 'gold' };
  }
  if (normalizedState === 'CLOSED' || normalizedState === 'REVOKED' || normalizedStatus === 'failed') {
    return { text: '已关闭', color: 'default' };
  }
  if (!normalizedState && !normalizedStatus) {
    return { text: '-', color: 'default' };
  }
  return { text: normalizedState || normalizedStatus, color: 'blue' };
};

const toUtcMs = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return 0;
  const dt = new Date(raw);
  const ms = dt.getTime();
  return Number.isFinite(ms) ? ms : 0;
};

const Billing = () => {
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState({ latest: null, active: null });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionPlanId, setActionPlanId] = useState('');
  const [error, setError] = useState('');
  const [checkout, setCheckout] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [historyItems, setHistoryItems] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const pollRef = useRef(null);
  const pollStartAtRef = useRef(0);
  const pollWarnedRef = useRef(false);
  const pollTokenRef = useRef(0);
  const pollBaselineEndMsRef = useRef(0);
  const isWechatBrowser = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    return /MicroMessenger/i.test(navigator.userAgent || '');
  }, []);

  const stopPolling = useCallback(() => {
    pollTokenRef.current += 1;
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    pollStartAtRef.current = 0;
    pollWarnedRef.current = false;
    pollBaselineEndMsRef.current = 0;
  }, []);

  const refreshSubscription = useCallback(async () => {
    try {
      const data = await getSubscription();
      if (data?.status !== 'success') {
        throw new Error(resolveErrorMessage(data?.error_message, '获取订阅信息失败'));
      }
      setSubscription({ latest: data.latest || null, active: data.active || null });
      return data;
    } catch (e) {
      setError(e?.message || String(e));
      return null;
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const planResp = await getBillingPlans();
      if (planResp?.status !== 'success') {
        throw new Error(resolveErrorMessage(planResp?.error_message, '获取套餐失败'));
      }
      setPlans(planResp.plans || []);
      await refreshSubscription();
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [refreshSubscription]);

  const startPolling = useCallback((outTradeNo = '', baselinePeriodEnd = '') => {
    stopPolling();
    const currentToken = pollTokenRef.current;
    pollBaselineEndMsRef.current = toUtcMs(baselinePeriodEnd);
    pollStartAtRef.current = Date.now();
    pollWarnedRef.current = false;

    const isPollingStale = () => currentToken !== pollTokenRef.current;
    const isSubscriptionUpgraded = (subData) => {
      const currentEndMs = toUtcMs(subData?.active?.current_period_end);
      if (!currentEndMs) return false;
      const baselineEndMs = pollBaselineEndMsRef.current;
      if (!baselineEndMs) return true;
      return currentEndMs > baselineEndMs;
    };

    pollRef.current = setInterval(async () => {
      if (outTradeNo) {
        try {
          const reconcile = await reconcilePaymentStatus(outTradeNo);
          if (isPollingStale()) return;
          const tradeState = String(reconcile?.trade_state || '').toUpperCase();
          if (reconcile?.status === 'success' && tradeState === 'SUCCESS') {
            const latest = await refreshSubscription();
            if (isPollingStale()) return;
            if (isSubscriptionUpgraded(latest)) {
              message.success('订阅已生效');
              setQrOpen(false);
              stopPolling();
              return;
            }
          }
        } catch (_) {
          // ignore reconcile errors during polling
        }
      }

      const latest = await refreshSubscription();
      if (isPollingStale()) return;
      if (isSubscriptionUpgraded(latest)) {
        message.success('订阅已生效');
        setQrOpen(false);
        stopPolling();
        return;
      }

      if (!pollWarnedRef.current && pollStartAtRef.current > 0 && Date.now() - pollStartAtRef.current > 8 * 60 * 1000) {
        pollWarnedRef.current = true;
        message.warning('支付结果同步较慢，可点击“刷新订阅状态”或稍后重试。');
      }
    }, 5000);
  }, [refreshSubscription, stopPolling]);

  const handleRefreshSubscription = useCallback(async () => {
    try {
      const outTradeNo = String(checkout?.out_trade_no || '').trim();
      if (outTradeNo) {
        try {
          await reconcilePaymentStatus(outTradeNo);
        } catch (_) {
          // best-effort reconcile
        }
      }
    } finally {
      await refreshSubscription();
    }
  }, [checkout?.out_trade_no, refreshSubscription]);

  useEffect(() => {
    loadAll().catch((e) => setError(e?.message || String(e)));
    return () => stopPolling();
  }, [loadAll, stopPolling]);

  const activePlan = useMemo(() => {
    const active = subscription.active;
    if (!active) return null;
    const planId = String(active.plan_id || '').trim();
    return plans.find((plan) => String(plan.plan_id || '').trim() === planId) || null;
  }, [plans, subscription.active]);

  const planNameMap = useMemo(() => {
    const map = {};
    (plans || []).forEach((plan) => {
      const pid = String(plan?.plan_id || '').trim();
      if (pid) map[pid] = plan?.name || pid;
    });
    return map;
  }, [plans]);

  const loadSubscriptionHistory = useCallback(async (page = 1) => {
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const resp = await getSubscriptionHistory({ page, page_size: HISTORY_PAGE_SIZE });
      if (resp?.status !== 'success') {
        throw new Error(resolveErrorMessage(resp?.error_message, '获取历史订阅记录失败'));
      }
      setHistoryItems(resp.items || []);
      setHistoryTotal(Number(resp.total || 0));
      setHistoryPage(Number(resp.page || page));
    } catch (e) {
      setHistoryError(e?.message || String(e));
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const handleOpenHistory = useCallback(() => {
    setHistoryOpen(true);
    loadSubscriptionHistory(1).catch(() => {});
  }, [loadSubscriptionHistory]);

  const handleCloseHistory = useCallback(() => {
    setHistoryOpen(false);
  }, []);

  const historyColumns = useMemo(() => ([
    {
      title: '套餐',
      key: 'plan',
      width: 180,
      render: (_, record) => {
        const planId = String(record?.plan_id || '').trim();
        const planName = String(record?.plan_name || '').trim() || planNameMap[planId] || planId || '-';
        return (
          <Space direction="vertical" size={0}>
            <Typography.Text strong>{planName}</Typography.Text>
            {planId && <Typography.Text type="secondary">ID: {planId}</Typography.Text>}
          </Space>
        );
      },
    },
    {
      title: '订阅周期',
      key: 'period',
      width: 220,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Typography.Text>{formatDateTime(record?.period_start)}</Typography.Text>
          <Typography.Text type="secondary">至 {formatDateTime(record?.period_end)}</Typography.Text>
        </Space>
      ),
    },
    {
      title: '支付',
      key: 'payment',
      width: 180,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Typography.Text>{formatPaymentAmount(record?.amount_cents, record?.currency)}</Typography.Text>
          <Typography.Text type="secondary">{formatDateTime(record?.paid_at)}</Typography.Text>
        </Space>
      ),
    },
    {
      title: '状态',
      key: 'status',
      width: 110,
      render: (_, record) => {
        const meta = resolveTradeStateMeta(record?.status, record?.trade_state);
        return <Tag color={meta.color}>{meta.text}</Tag>;
      },
    },
    {
      title: '订单号',
      dataIndex: 'payment_id',
      key: 'payment_id',
      width: 320,
      render: (value, record) => (
        <Typography.Text
          className="billing-order-id"
          type="secondary"
          title={value || record?.out_trade_no || ''}
        >
          {value || record?.out_trade_no || '-'}
        </Typography.Text>
      ),
    },
  ]), [planNameMap]);

  const handleCheckout = async (plan) => {
    if (actionLoading) {
      message.info('正在创建支付订单，请稍候');
      return;
    }
    const currentPlanId = String(plan?.plan_id || '').trim();
    if (!currentPlanId) return;
    setActionLoading(true);
    setActionPlanId(currentPlanId);
    setError('');
    try {
      const baselinePeriodEnd = String(subscription?.active?.current_period_end || '').trim();
      const channelCode = isWechatBrowser ? 'wx_pub' : 'wx_native';
      const resp = await createCheckout({
        plan_id: currentPlanId,
        channel_code: channelCode,
      });
      if (resp?.status !== 'success') {
        throw new Error(resolveErrorMessage(resp?.error_message, '创建支付失败'));
      }
      if (channelCode === 'wx_pub') {
        const jsapi = resp?.jsapi;
        if (!jsapi) {
          throw new Error('未拿到公众号支付参数，请检查支付配置');
        }
        await new Promise((resolve, reject) => {
          const onInvoke = () => {
            if (!window.WeixinJSBridge || !window.WeixinJSBridge.invoke) {
              reject(new Error('当前环境不支持微信内支付'));
              return;
            }
            window.WeixinJSBridge.invoke('getBrandWCPayRequest', jsapi, (res) => {
              const errMsg = String(res?.err_msg || '').toLowerCase();
              if (errMsg.includes('ok')) {
                resolve(true);
                return;
              }
              if (errMsg.includes('cancel')) {
                reject(new Error('你已取消支付'));
                return;
              }
              reject(new Error(`微信支付失败: ${res?.err_msg || 'unknown_error'}`));
            });
          };
          if (window.WeixinJSBridge && window.WeixinJSBridge.invoke) {
            onInvoke();
            return;
          }
          const readyHandler = () => {
            document.removeEventListener('WeixinJSBridgeReady', readyHandler);
            onInvoke();
          };
          document.addEventListener('WeixinJSBridgeReady', readyHandler, { once: true });
          setTimeout(() => {
            document.removeEventListener('WeixinJSBridgeReady', readyHandler);
            reject(new Error('未检测到微信支付环境'));
          }, 5000);
        });
        message.info('请在微信支付弹窗中完成付款');
        setCheckout({
          out_trade_no: resp.out_trade_no,
          plan: resp.plan || plan,
        });
        startPolling(resp.out_trade_no, baselinePeriodEnd);
        return;
      }
      const data = {
        code_url: resp.code_url,
        out_trade_no: resp.out_trade_no,
        plan: resp.plan || plan,
      };
      if (!data.code_url) {
        throw new Error('未拿到二维码链接，请检查支付配置');
      }
      setCheckout(data);
      setQrOpen(true);
      startPolling(resp.out_trade_no, baselinePeriodEnd);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setActionLoading(false);
      setActionPlanId('');
    }
  };

  const handleQrClose = () => {
    setQrOpen(false);
    stopPolling();
  };

  const currentStatus = subscription.active ? 'active' : subscription.latest ? 'expired' : 'none';

  return (
    <div className="billing-page">
      <Spin spinning={loading}>
        <Row gutter={[16, 16]}>
          <Col xs={24} xl={10}>
            <Card className="billing-card" title="当前订阅">
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                {currentStatus === 'active' ? (
                  <>
                    <div className="billing-title">
                      <Typography.Title level={4} style={{ margin: 0 }}>
                        {activePlan?.name || '订阅已生效'}
                      </Typography.Title>
                      <Tag color="green">有效</Tag>
                    </div>
                    <div className="billing-meta">
                      到期时间：
                      <span className="billing-meta__value">{formatDateTime(subscription.active?.current_period_end)}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="billing-title">
                      <Typography.Title level={4} style={{ margin: 0 }}>
                        暂未开通
                      </Typography.Title>
                      <Tag color={currentStatus === 'expired' ? 'orange' : 'default'}>
                        {currentStatus === 'expired' ? '已到期' : '未订阅'}
                      </Tag>
                    </div>
                    <div className="billing-meta">
                      最近订阅：
                      <span className="billing-meta__value">{formatDateTime(subscription.latest?.current_period_end)}</span>
                    </div>
                  </>
                )}
                <Divider style={{ margin: '8px 0' }} />
                <Alert
                  type="info"
                  showIcon
                  message="手动续费"
                  description="支持手动购买，到期前后均可再次购买，新的有效期会自动叠加。"
                />
                <div>
                  <Space size={12} wrap>
                    <Button onClick={handleRefreshSubscription} type="link" style={{ paddingLeft: 0 }}>
                      刷新订阅状态
                    </Button>
                    <Button onClick={handleOpenHistory} type="link" style={{ paddingLeft: 0 }}>
                      历史订阅记录
                    </Button>
                  </Space>
                </div>
              </Space>
            </Card>
          </Col>

          <Col xs={24} xl={14}>
            <Card className="billing-card" title="订阅套餐">
              {error && (
                <Alert
                  type="error"
                  showIcon
                  message="加载失败"
                  description={error}
                  style={{ marginBottom: 12 }}
                />
              )}
              <Row gutter={[12, 12]}>
                {(plans || []).map((plan) => (
                  <Col xs={24} md={12} xl={12} key={plan.plan_id}>
                    <Card className="plan-card" bodyStyle={{ padding: 16 }}>
                      <Space direction="vertical" size={6} style={{ width: '100%' }}>
                        <div className="plan-card__title">
                          <Typography.Text strong>{plan.name || plan.plan_id}</Typography.Text>
                          <Tag color="blue">{resolveDurationLabel(plan.duration_days)}</Tag>
                        </div>
                        <div className="plan-card__price">
                          <span className="plan-card__currency">¥</span>
                          <span className="plan-card__amount">{formatPrice(plan.price_cents)}</span>
                        </div>
                        <div className="plan-card__limits">
                          <Tag>账号 {plan?.limits?.max_accounts ?? '-'}</Tag>
                          <Tag>任务 {plan?.limits?.max_tasks ?? '-'}</Tag>
                          <Tag>月发布 {plan?.limits?.monthly_publish_limit ?? '-'}</Tag>
                        </div>
                        <Button
                          type="primary"
                          block
                          loading={actionLoading && actionPlanId === String(plan.plan_id || '')}
                          onClick={() => handleCheckout(plan)}
                        >
                          {isWechatBrowser ? '微信内支付' : '微信扫码购买'}
                        </Button>
                      </Space>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card>
          </Col>
        </Row>
      </Spin>

      <Drawer
        title="历史订阅记录"
        placement="right"
        width="min(760px, 92vw)"
        open={historyOpen}
        onClose={handleCloseHistory}
        extra={(
          <Button type="link" onClick={() => loadSubscriptionHistory(historyPage)} loading={historyLoading}>
            刷新
          </Button>
        )}
      >
        {historyError && (
          <Alert
            type="error"
            showIcon
            message="加载失败"
            description={historyError}
            style={{ marginBottom: 12 }}
          />
        )}
        <Table
          className="billing-history-table"
          size="middle"
          rowKey={(record) => String(record?.history_id || record?.payment_id || record?.out_trade_no || '')}
          dataSource={historyItems}
          columns={historyColumns}
          loading={historyLoading}
          pagination={{
            current: historyPage,
            pageSize: HISTORY_PAGE_SIZE,
            total: historyTotal,
            showSizeChanger: false,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page) => loadSubscriptionHistory(page),
          }}
          scroll={{ x: 860 }}
          locale={{
            emptyText: historyLoading
              ? '加载中...'
              : <Empty description="暂无历史订阅记录" image={Empty.PRESENTED_IMAGE_SIMPLE} />,
          }}
        />
      </Drawer>

      <Modal
        title="微信支付"
        open={qrOpen}
        onCancel={handleQrClose}
        footer={null}
        destroyOnClose
      >
        <Space direction="vertical" size={16} style={{ width: '100%', alignItems: 'center' }}>
          <div className="qr-summary">
            <Typography.Text strong>{checkout?.plan?.name || '订阅订单'}</Typography.Text>
            <div className="qr-price-card">
              <Typography.Text type="secondary">应付金额</Typography.Text>
              <Typography.Title level={3} style={{ margin: 0 }}>
                {formatPaymentAmount(checkout?.plan?.price_cents, checkout?.plan?.currency)}
              </Typography.Title>
              <Typography.Text type="secondary">
                有效期：{resolveDurationLabel(checkout?.plan?.duration_days)}
              </Typography.Text>
            </div>
            <Typography.Text type="secondary">
              订单号 {checkout?.out_trade_no || '-'}
            </Typography.Text>
          </div>
          {checkout?.code_url && <QRCode value={checkout.code_url} size={180} />}
          <Alert
            type="info"
            showIcon
            message="扫码支付后请稍候"
            description="支付成功后会自动刷新订阅状态，也可以手动点击“刷新订阅状态”。"
            style={{ width: '100%' }}
          />
          <Button onClick={handleRefreshSubscription} block>
            刷新订阅状态
          </Button>
        </Space>
      </Modal>
    </div>
  );
};

export default Billing;
